// components/OttoCalSync.tsx
// 화면에는 알림만 보이는 보조 컴포넌트. Otto 화면이 열릴 때 한 번:
//  - 보드에 저장된 보정값이 있으면 → 이 컴퓨터에도 같은 값을 보관해 둔다.
//  - 보드에 저장된 값이 없는데(펌웨어를 다시 설치한 직후 등) 이 컴퓨터에 보관한 값이 있으면 → 자동으로 보드에 적용하고 저장한다.
// 보드에서 저장/기본값 응답이 오면 이 컴퓨터의 보관값도 같이 갱신/삭제한다.
import React, { useEffect, useRef, useState } from 'react';
import MQTTService from './MQTTService';
import { clearStoredCal, loadStoredCal, saveStoredCal, isValidCal, OttoCalValues } from './utils/ottoCalStore';

interface Props {
  mac: string;
  email: string;
  mqttMessage?: string; // App 이 내려주는 최근 MQTT 메시지(JSON 문자열)
}

// 보드 응답(m)에서 보정값 6개를 꺼낸다
function valuesFrom(m: any): OttoCalValues | null {
  const v = { yl: m.calYl, yr: m.calYr, tr: m.calTiltR, tl: m.calTiltL, rl: m.calSpeedL, rr: m.calSpeedR };
  return isValidCal(v) ? v : null;
}

const fmt = (at: number) => (at ? new Date(at).toLocaleString('ko-KR') : '');

const OttoCalSync: React.FC<Props> = ({ mac, email, mqttMessage }) => {
  const [notice, setNotice] = useState('');
  const tried = useRef(false); // 자동 적용은 화면을 연 뒤 한 번만 한다

  const pub = (payload: Record<string, any>) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'ottoCal', mac, email, ...payload })
    );
  };

  // 화면을 열면 보드의 현재 보정값을 한 번 조회한다
  useEffect(() => {
    tried.current = false;
    pub({ action: 'get' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mac, email]);

  useEffect(() => {
    if (!mqttMessage) return;
    try {
      const m = JSON.parse(mqttMessage);
      if (m.mac !== mac || m.command !== 'ottoCal') return;
      if (m.ok === 0 || m.ok === false) return;
      const v = valuesFrom(m);
      if (!v) return;

      if (m.action === 'save' || m.action === 'restore') {
        saveStoredCal(mac, v); // 보드에 저장됐으면 이 컴퓨터에도 보관한다
        if (m.action === 'restore') setNotice('보정값을 자동으로 적용했습니다.');
        return;
      }
      if (m.action === 'clear') {
        clearStoredCal(mac); // 기본값으로 되돌렸으면 이 컴퓨터의 보관값도 지운다 (다시 자동 적용되지 않게)
        return;
      }
      if (m.action === 'get' && !tried.current) {
        tried.current = true;
        if (m.calSaved === 1) {
          saveStoredCal(mac, v); // 보드의 값이 기준: 이 컴퓨터에도 같은 값을 보관해 둔다
        } else {
          const stored = loadStoredCal(mac);
          if (stored) {
            // 보드에 저장된 값이 없고 이 컴퓨터에 보관한 값이 있으면 자동으로 적용한다
            pub({
              action: 'restore',
              calYl: stored.v.yl, calYr: stored.v.yr,
              calTiltR: stored.v.tr, calTiltL: stored.v.tl,
              calSpeedL: stored.v.rl, calSpeedR: stored.v.rr,
            });
            setNotice(`이 컴퓨터에 보관한 보정값(${fmt(stored.at)})을 보드에 적용하는 중입니다.`);
          }
        }
      }
    } catch (e) {
      // 다른 메시지는 무시한다
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mqttMessage]);

  if (!notice) return null;
  return (
    <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#ecfdf5', border: '1px solid #34d399', borderRadius: '8px', color: '#065f46', fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
      <span>✅ {notice}</span>
      <button type="button" onClick={() => setNotice('')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#065f46', fontWeight: 'bold' }}>닫기</button>
    </div>
  );
};

export default OttoCalSync;
